/**
 * useTransientReactions — client-side transient reaction manager.
 * 
 * Handles real-time visual reactions (block found, fee spike, etc.)
 * with proper lifecycle: trigger → active → cleanup → baseline.
 * 
 * Key guarantees:
 * - Only ONE transient active at a time (priority-based)
 * - Every transient has a hard TTL — never stays forever
 * - Force-cleanup guard catches any leaked state
 * - Clean return-to-baseline after every transient
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { TransientReactionType } from '@/lib/types/events';
import { TRANSIENT_REACTIONS } from '@/config/buddyExpressions';

// Priority map — higher number = higher priority = can override lower
const TRANSIENT_PRIORITY: Record<TransientReactionType, number> = {
  halvingHype: 110,
  blockFound: 100,
  backToBack: 95,
  fearSurge: 85,
  greedPeak: 82,
  feeSpike: 80,
  volatilityShock: 70,
  feeRelief: 65,
  pricePump: 60,
  priceDip: 55,
  droughtAnxiety: 45,
  longWait: 40,
};

// Hard maximum duration — safety net (no transient should ever exceed this)
const MAX_TRANSIENT_MS = 4000;

// Minimum gap between transients for clean visual reset
const RECOVERY_GAP_MS = 300;

interface TransientState {
  type: TransientReactionType;
  startedAt: number;
  expiresAt: number;
  priority: number;
}

export interface TransientManagerOutput {
  activeTransient: TransientReactionType | null;
  triggerTransient: (type: TransientReactionType) => void;
  /** Debug info */
  debug: {
    current: TransientState | null;
    lastCleared: number;
    triggerCount: number;
  };
}

export function useTransientReactions(): TransientManagerOutput {
  const [active, setActive] = useState<TransientState | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const recoveryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const lastClearedRef = useRef(0);
  const triggerCountRef = useRef(0);
  const activeRef = useRef<TransientState | null>(null);

  // Keep ref in sync for use in callbacks
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  const clearTransient = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
    setActive(null);
    activeRef.current = null;
    lastClearedRef.current = Date.now();
  }, []);

  const triggerTransient = useCallback((type: TransientReactionType) => {
    const now = Date.now();
    const priority = TRANSIENT_PRIORITY[type] ?? 50;
    const config = TRANSIENT_REACTIONS[type as keyof typeof TRANSIENT_REACTIONS];
    if (!config) return;

    // Enforce recovery gap — don't immediately start new transient
    const timeSinceLastClear = now - lastClearedRef.current;
    if (timeSinceLastClear < RECOVERY_GAP_MS && activeRef.current === null) {
      // Delay slightly
      recoveryTimerRef.current = setTimeout(() => {
        triggerTransient(type);
      }, RECOVERY_GAP_MS - timeSinceLastClear);
      return;
    }

    // If another transient is active, only override if higher priority
    if (activeRef.current) {
      if (priority <= activeRef.current.priority) {
        return; // Lower priority — ignore
      }
      // Higher priority — clear old one first
      if (timerRef.current) clearTimeout(timerRef.current);
    }

    const duration = Math.min(config.durationMs, MAX_TRANSIENT_MS);
    const expiresAt = now + duration;

    const newState: TransientState = {
      type,
      startedAt: now,
      expiresAt,
      priority,
    };

    triggerCountRef.current++;
    setActive(newState);
    activeRef.current = newState;

    // Schedule cleanup
    timerRef.current = setTimeout(() => {
      clearTransient();
    }, duration);
  }, [clearTransient]);

  // Safety guard: force-clear any transient that exceeds MAX_TRANSIENT_MS
  useEffect(() => {
    const guard = setInterval(() => {
      if (activeRef.current && Date.now() > activeRef.current.expiresAt + 500) {
        console.warn('[TransientManager] Force-clearing leaked transient:', activeRef.current.type);
        clearTransient();
      }
    }, 1000);
    return () => clearInterval(guard);
  }, [clearTransient]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
    };
  }, []);

  return {
    activeTransient: active?.type ?? null,
    triggerTransient,
    debug: {
      current: active,
      lastCleared: lastClearedRef.current,
      triggerCount: triggerCountRef.current,
    },
  };
}
